import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { Toaster } from "sonner";
import SiteLayout from "@/components/site/SiteLayout";
import AnalyticsTracker from "@/components/site/AnalyticsTracker";

// Public pages
import CmsPage from "@/pages/CmsPage";
import HomeLanding from "@/pages/HomeLanding";
import ProductDetail from "@/pages/ProductDetail";
import ArticleDetail from "@/pages/ArticleDetail";
import OrderSample from "@/pages/OrderSample";
import Career from "@/pages/Career";
import Live from "@/pages/Live";
import NotFound from "@/pages/NotFound";
import PageRenderer from "@/pages/PageRenderer";

// Admin
import AdminLogin from "@/pages/admin/Login";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminArticles from "@/pages/admin/AdminArticles";
import AdminGallery from "@/pages/admin/AdminGallery";
import AdminFAQ from "@/pages/admin/AdminFAQ";
import AdminTestimonials from "@/pages/admin/AdminTestimonials";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminMedia from "@/pages/admin/AdminMedia";
import AdminPages from "@/pages/admin/AdminPages";
import PageEditor from "@/pages/admin/PageEditor";
import AdminActivity from "@/pages/admin/AdminActivity";
import AdminBackup from "@/pages/admin/AdminBackup";
import AdminCareers from "@/pages/admin/AdminCareers";
import AdminLives from "@/pages/admin/AdminLives";

const RequireAuth = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-zinc-500">Loading...</div>;
  if (!user) return <Navigate to="/admin/login" state={{ from: location }} replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <LanguageProvider>
        <BrowserRouter>
          <AnalyticsTracker />
          <Toaster position="top-right" richColors />
          <Routes>
            <Route element={<SiteLayout />}>
              <Route path="/" element={<HomeLanding />} />
              <Route path="/tentang" element={<CmsPage routeKey="about" />} />
              <Route path="/layanan" element={<CmsPage routeKey="services" />} />
              <Route path="/produk" element={<CmsPage routeKey="products" />} />
              <Route path="/produk/:slug" element={<ProductDetail />} />
              <Route path="/artikel" element={<CmsPage routeKey="articles" />} />
              <Route path="/artikel/:slug" element={<ArticleDetail />} />
              <Route path="/galeri" element={<CmsPage routeKey="gallery" />} />
              <Route path="/faq" element={<CmsPage routeKey="faq" />} />
              <Route path="/kontak" element={<CmsPage routeKey="contact" />} />
              <Route path="/order-sample" element={<OrderSample />} />
              <Route path="/karir" element={<Career />} />
              <Route path="/live" element={<Live />} />
              <Route path="/p/:slug" element={<PageRenderer />} />
            </Route>

            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<RequireAuth><AdminLayout /></RequireAuth>}>
              <Route index element={<Dashboard />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="articles" element={<AdminArticles />} />
              <Route path="gallery" element={<AdminGallery />} />
              <Route path="careers" element={<AdminCareers />} />
              <Route path="lives" element={<AdminLives />} />
              <Route path="faq" element={<AdminFAQ />} />
              <Route path="testimonials" element={<AdminTestimonials />} />
              <Route path="leads" element={<AdminLeads />} />
              <Route path="media" element={<AdminMedia />} />
              <Route path="pages" element={<AdminPages />} />
              <Route path="pages/:id" element={<PageEditor />} />
              <Route path="activity" element={<AdminActivity />} />
              <Route path="backup" element={<AdminBackup />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
        </LanguageProvider>
      </SettingsProvider>
    </AuthProvider>
  );
}

export default App;
